import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import QRCode from "qrcode";
import { Copy, Share2, Link2, XCircle } from "lucide-react";
import { Button } from "../ui/button";
import { contractsApi } from "../../lib/contractsApi";

// Secure signing link for the other party: copy / native share / QR (no email needed).
export const ContractSendBox = ({ contract, onChange }) => {
  const [busy, setBusy] = useState(false);
  const canvas = useRef(null);
  const url = contract.token ? `${window.location.origin}/contract/${contract.token}` : "";
  useEffect(() => { if (url && canvas.current) QRCode.toCanvas(canvas.current, url, { width: 132, margin: 1 }); }, [url]);
  const send = async () => {
    setBusy(true);
    try { const r = await contractsApi.send(contract.id); onChange({ ...contract, token: r.token, status: "sent" }); toast.success("تم إنشاء رابط التوقيع الآمن"); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الإرسال"); } finally { setBusy(false); }
  };
  const revoke = async () => {
    if (!window.confirm("إلغاء الرابط؟ لن يستطيع الطرف الآخر فتحه.")) return;
    await contractsApi.revoke(contract.id); onChange({ ...contract, token: null, status: "draft" }); toast.success("تم إلغاء الرابط");
  };
  const copy = async () => { await navigator.clipboard.writeText(url); toast.success("تم نسخ الرابط"); };
  const share = async () => {
    try { await navigator.share({ title: contract.title, text: `عقد ${contract.number} — يرجى الاطلاع والتوقيع`, url }); }
    catch { copy(); }
  };
  if (contract.status === "signed") return null;
  return (
    <div className="rounded-2xl border border-blue-200 bg-blue-50/50 p-3 space-y-2" data-testid="contract-send-box">
      <p className="text-sm font-bold text-blue-800 flex items-center gap-1.5"><Link2 className="h-4 w-4" /> إرسال للطرف الثاني للتوقيع</p>
      {!url ? (
        <>
          <p className="text-[11px] text-slate-600">ينشئ رابطاً آمناً خاصاً بهذا العقد — يفتحه الطرف الثاني من أي جهاز ويوقّع بإصبعه دون تسجيل دخول.</p>
          <Button onClick={send} disabled={busy || !contract.id} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="contract-send-btn">إنشاء رابط التوقيع</Button>
          {!contract.id && <p className="text-[11px] text-amber-700">احفظ العقد أولاً.</p>}
        </>
      ) : (
        <>
          <div className="flex gap-2 items-center">
            <canvas ref={canvas} className="rounded-lg bg-white border shrink-0" data-testid="contract-qr" />
            <div className="min-w-0 space-y-1.5 flex-1">
              <p className="text-[11px] text-slate-500 break-all" dir="ltr" data-testid="contract-link">{url}</p>
              <div className="flex flex-wrap gap-1.5">
                <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" onClick={copy} data-testid="contract-copy-link"><Copy className="h-3.5 w-3.5" /> نسخ</Button>
                <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" onClick={share} data-testid="contract-share-link"><Share2 className="h-3.5 w-3.5" /> مشاركة</Button>
                <Button size="sm" variant="ghost" className="h-8 gap-1 text-xs text-rose-600" onClick={revoke} data-testid="contract-revoke-link"><XCircle className="h-3.5 w-3.5" /> إلغاء</Button>
              </div>
            </div>
          </div>
          <p className="text-[11px] text-slate-500">{contract.opened_at ? "✓ فتح الطرف الثاني الرابط" : "لم يُفتح بعد"} — امسح الرمز بالجوال أو أرسل الرابط بأي وسيلة.</p>
        </>
      )}
    </div>
  );
};
